import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquare, X, Send, Loader2, Sparkles } from "lucide-react";

const STORAGE_KEY = "gmb_chat_session_id";
const SUGGESTIONS = [
  "Che cos'è la sienite della Balma?",
  "Quali finiture sono disponibili?",
  "Come arrivo a Campiglia Cervo?",
  "Da quanto tempo siete attivi?",
];

export default function ChatBot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      text:
        "Sono Picapere, l'assistente della cava di GMB Graniti. Chiedimi della sienite, della storia della Balma o di come trovarci.",
    },
  ]);
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) || null;
    } catch {
      return null;
    }
  });
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading, open]);

  const send = async (text) => {
    const value = (text ?? draft).trim();
    if (!value || loading) return;
    setMessages((m) => [...m, { role: "user", text: value }]);
    setDraft("");
    setLoading(true);
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/chat`;
      const res = await axios.post(url, { message: value, session_id: sessionId });
      const { reply, session_id } = res.data;
      if (session_id && session_id !== sessionId) {
        setSessionId(session_id);
        try {
          localStorage.setItem(STORAGE_KEY, session_id);
        } catch {
          /* ignore */
        }
      }
      setMessages((m) => [...m, { role: "assistant", text: reply }]);
    } catch (err) {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          text:
            "Mi spiace, qualcosa è andato storto. Riprova fra poco o scrivici direttamente da “Contatti”.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {/* Toggle button */}
      <motion.button
        data-testid="chatbot-toggle"
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-5 left-5 z-[60] flex items-center gap-2 px-4 py-3 bg-[var(--accent)] text-[#0a0a0a] font-medium tracking-[0.05em] uppercase text-[0.72rem] shadow-[0_8px_30px_rgba(0,0,0,0.5)] hover:bg-[var(--accent-hover)] transition-colors"
        whileHover={{ y: -2 }}
        whileTap={{ scale: 0.97 }}
        aria-label={open ? "Chiudi chat" : "Apri chat"}
      >
        {open ? <X size={16} /> : <MessageSquare size={16} />}
        <span className="hidden sm:inline">{open ? "Chiudi" : "Chiedi al Picapere"}</span>
        {!open && <Sparkles size={12} className="opacity-60" />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chatbot-panel"
            initial={{ opacity: 0, y: 30, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.96 }}
            transition={{ duration: 0.28, ease: [0.2, 0.8, 0.2, 1] }}
            className="fixed bottom-24 left-5 z-[60] w-[calc(100vw-2.5rem)] sm:w-[400px] md:w-[420px] h-[min(560px,75vh)] bg-[var(--surface-1)] border border-[var(--border-strong)] backdrop-blur-xl flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border-subtle)]">
              <div className="flex items-center gap-3">
                <span className="block w-2 h-2 rotate-45 bg-[var(--accent)]" />
                <div className="leading-none">
                  <p className="font-display text-sm tracking-[0.18em] uppercase text-neutral-100">
                    Il Picapere
                  </p>
                  <p className="font-editorial italic text-xs text-[var(--accent)] mt-1">
                    assistente · GMB Graniti
                  </p>
                </div>
              </div>
              <button
                data-testid="chatbot-close"
                onClick={() => setOpen(false)}
                className="text-neutral-500 hover:text-neutral-100 transition-colors"
                aria-label="Chiudi"
              >
                <X size={16} />
              </button>
            </div>

            {/* Messages */}
            <div
              ref={scrollRef}
              className="flex-1 overflow-y-auto px-5 py-4 space-y-4"
              data-testid="chatbot-messages"
            >
              {messages.map((m, i) => (
                <div
                  key={i}
                  className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[85%] px-4 py-3 text-[0.92rem] leading-relaxed whitespace-pre-wrap ${
                      m.role === "user"
                        ? "bg-[var(--accent)] text-[#0a0a0a]"
                        : "bg-[var(--surface-2)] text-neutral-200 border border-[var(--border-subtle)]"
                    }`}
                  >
                    {m.text}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-[var(--surface-2)] border border-[var(--border-subtle)] px-4 py-3 flex items-center gap-2 text-neutral-400 text-sm">
                    <Loader2 size={14} className="animate-spin" />
                    sto cavando una risposta…
                  </div>
                </div>
              )}

              {messages.length === 1 && !loading && (
                <div className="pt-2">
                  <p className="text-[0.65rem] tracking-[0.25em] uppercase text-neutral-500 mb-3">
                    Suggerimenti
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {SUGGESTIONS.map((s, i) => (
                      <button
                        key={i}
                        onClick={() => send(s)}
                        className="text-xs text-neutral-300 border border-[var(--border-subtle)] px-3 py-2 hover:border-[var(--accent)] hover:text-[var(--accent)] transition-colors text-left"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Input */}
            <div className="border-t border-[var(--border-subtle)] p-3 flex items-center gap-2">
              <input
                data-testid="chatbot-input"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={onKey}
                placeholder="Scrivi una domanda…"
                className="input-base flex-1"
                disabled={loading}
              />
              <button
                data-testid="chatbot-send"
                onClick={() => send()}
                disabled={loading || !draft.trim()}
                className="btn-primary px-3 py-3 disabled:opacity-50"
                aria-label="Invia"
              >
                <Send size={14} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
